/**
 * Machine guns and the GUN JAMS ALLOWED toggle. Manual, verbatim:
 *
 * > "Occasionally, your machine guns will jam. Firing a long burst from your
 * > machine gun has a greater chance of jamming your guns than a short burst...
 * > If your guns are jammed, you may try to unjam them by repeatedly pressing
 * > the U key. This won't always work, but keep trying. NOTE: it is possible
 * > for your machine guns to jam permanently."
 *
 * The jam rate is *global*, not per-aircraft: offset 208 is 200 in all 33
 * files (flight-tunables.md §6). Scott Mobley's measured response curve for
 * that field puts the shipped setting at "~300 rounds, jam after 5–6 s of
 * continuous fire", which is the single calibration point the burst scaling
 * hangs off — a live-footage calibration, independent of what offset 208
 * itself turned out to be. AD-011 §4.1 (`gun-mounts-design.md`) later found no
 * `mg.c` code that reads 208 at all: the game's real jam table sits at
 * offsets 210/212/214 instead, `[40, 20, 30]` on every file and selected by a
 * per-shot field this port had not traced.
 *
 * **The selector is traced now (G35, 2026-08-27): it is the mission's ammunition
 * type**, `{regular 40, incendiary 20, ? 30}`, so incendiary jams sooner — the
 * Buckingham reputation exactly. `jamProbability` below consumes it through
 * `ammunitionJamScale`, which is derived from the original's own formula rather
 * than picked. What it deliberately does **not** do is replace Mobley's absolute
 * rate; see that function for the measurement conflict that decision turns on.
 *
 * Gun *effectiveness* (offset 206) is a separate damage multiplier that is not
 * simply gun count — the Pup and S.E.5a carry 1300 on one gun.
 *
 * Ballistics and hit resolution belong to the weapons lane; this module owns
 * ammunition, the burst counter, and the jam state machine.
 */

/**
 * The two ammunition types `PS.EXE`/`RBMB.EXE` name verbatim
 * (docs/game/missions.md). Ordinary ball rounds pass through a hydrogen bag
 * without igniting it; the Buckingham/Pomeroy/Brock incendiary rounds are what
 * actually set balloons alight, and the game's own loadout choice exists for
 * that reason — plus, since G35, a real cost on the shooter's own aeroplane:
 * they scatter (`combat/gunnery.ts`) and they jam sooner (below).
 *
 * Declared here rather than in `combat/target-kinds.ts`, which re-exports them:
 * the jam threshold reads them from inside the sim, and `flight/` may not import
 * from `combat/`.
 */
export const AMMUNITION_TYPES = ["regular", "incendiary"] as const;

export type AmmunitionType = (typeof AMMUNITION_TYPES)[number];

import type { Vec3 } from "./math3d.ts";
import type { RngBank } from "./rng.ts";
import type { Tunables } from "./tunables.ts";

/**
 * One gun *station* off the `.DAT` gun block (`aircraft-gun-table.ts`), with
 * its position already carried into the engine's body frame.
 *
 * A station is not a barrel: the centred-twin mount class is one record that
 * fires two barrels, which is why `barrels` is read off the mount byte and
 * not off the station count.
 */
export interface GunMountSpec {
  /** Position in file order. Station 0 is flexible on every multi-station aircraft; the last is the fixed forward gun. */
  readonly index: number;
  /** `+6` mount class — 0 centred twin, 2 single left, 4 single right, 6 single centred. */
  readonly mountClassRaw: number;
  /** Barrels this station fires from: 2 for the centred-twin class, 1 otherwise. */
  readonly barrels: number;
  /** `+0/+2/+4` verbatim, in `.TBL` model units (right / nose-forward / up). */
  readonly rightUnits: number;
  readonly forwardUnits: number;
  readonly upUnits: number;
  /** The same point in body-frame metres from the pose origin — `aircraft-model-frame.ts` does the conversion. */
  readonly bodyOffsetM: Vec3;
  /** `+8`…`+14` verbatim, BAM16 — the mount's **traverse** arcs (`combat/traverse.ts`). */
  readonly verticalFromBam: number;
  readonly verticalToBam: number;
  readonly horizontalFromBam: number;
  readonly horizontalToBam: number;
  /** `+8`/`+10` in radians: how far the mount may be depressed and elevated, positive up. */
  readonly elevationFromRad: number;
  readonly elevationToRad: number;
  /** `+12`/`+14` in radians: how far it may be traversed, positive to the right. */
  readonly azimuthFromRad: number;
  readonly azimuthToRad: number;
  /** True when the azimuth arc runs the long way round through ±π — a gunner's rear arc. */
  readonly wrapsRear: boolean;
  /**
   * True when the arc is a gunner's hemisphere rather than a fixed gun's
   * symmetric tolerance band. Derived from the arc itself
   * (`horizontalFrom > horizontalTo` — a wrapped rear arc), not from the
   * station index.
   */
  readonly flexible: boolean;
}

export interface GunSpec {
  /** `.DAT` offset 216 — 1 / 2 / 3 gun *stations*; `fileSize == 234 + 16·(n−1)` on every file. Not a barrel count. */
  readonly positions: number;
  /** Every station's geometry and arc, in file order. */
  readonly mounts: readonly GunMountSpec[];
  /**
   * The station that fires down the boresight: the last one, which is the
   * fixed forward gun on 28 of the 33. On the five gunner-armed types
   * (`BE2`, `AVIATIK`, `FE2`, `GOTHA`, `HANDLEY`) every station is flexible
   * and this one's `flexible` flag says so; those aircraft fire through
   * `combat/traverse.ts`'s arc selection instead, so their rounds leave the
   * station that can actually be brought onto the target and nothing leaves
   * at all when none can.
   */
  readonly forwardMount: GunMountSpec;
  /** False on those five: the aircraft has no fixed forward gun in the data at all. */
  readonly hasFixedGun: boolean;
  /** Barrels the forward station fires from: 2 on the eleven centred-twin scouts, 1 on the rest. */
  readonly barrels: number;
  /** `.DAT` offset 206 — gun effectiveness, 800 / 900 / 1024 / 1300. A damage multiplier, not a count. */
  readonly damageRaw: number;
  /**
   * `.DAT` offset 208 — 200 on all 33 aircraft. What `jamProbability` scales
   * on; see the module note for why.
   *
   * **And the original never reads the shipped value.** `mg.c+0x14d` walks
   * `−1 … 32` at mission load and overwrites `gunParams[+0x02]` with something
   * derived from offset **206**, clamped to `≤ 0x55`
   * (`gun-mounts-design.md` §10, from `zeppelin-guns.md` §5 — REPORTED). The
   * port is unaffected today, because `gunJamThresholdReference` is also 200
   * and the ratio is exactly 1 fleet-wide; but a lane rewiring the jam model
   * must not treat this field as an input to anything.
   */
  readonly jamThresholdRaw: number;
  /**
   * `.DAT` offsets 210/212/214 — `[40, 20, 30]` on all 33 aircraft, `mg.c`'s
   * real jam table (AD-011 §4.1). Reference data only: the port has not traced
   * which entry a given gun uses (`aircraft[+0x6a]`), so `jamProbability` does
   * not consume this — wiring a guessed index in would silently change every
   * aircraft's jam odds on invented data. Carried here so the next lane that
   * pins the selector has the values already in the fleet spec.
   */
  readonly jamThresholdTable: readonly [number, number, number];
  readonly roundsPerGun: number;
  readonly roundsPerSecond: number;
}

export interface GunState {
  readonly rounds: number;
  /** `roundsPerGun × positions` — the magazine size for a "453/500" readout. Constant for the life of a loadout. */
  readonly capacity: number;
  /** Rounds fired in the current uninterrupted burst — the manual's jam driver. */
  readonly burstRounds: number;
  readonly jammed: boolean;
  readonly permanentJam: boolean;
  readonly clearAttempts: number;
  readonly fireAccumulator: number;
  /** Previous tick's U key, so one press is one clear attempt. */
  readonly clearHeld: boolean;
}

export interface GunUpdate {
  readonly gun: GunSpec;
  readonly tunables: Tunables;
  readonly jamsEnabled: boolean;
  /**
   * The belt this sortie is flying (`SimConfig.ammunition`). Picks the entry of
   * `GunSpec.jamThresholdTable` in force — incendiary jams sooner.
   *
   * Optional, and absent means ball, so a hand-made test rig that predates the
   * field gets the jam rate it always had.
   */
  readonly ammunition?: AmmunitionType;
  readonly limitedAmmunition: boolean;
  readonly firing: boolean;
  readonly clearRequested: boolean;
  readonly rng: RngBank;
  readonly dt: number;
}

export interface GunResult {
  readonly guns: GunState;
  readonly roundsFired: number;
  readonly jammedNow: boolean;
  readonly permanentNow: boolean;
}

export function loadedGuns(gun: GunSpec): GunState {
  const capacity = gun.roundsPerGun * gun.positions;
  return {
    rounds: capacity,
    capacity,
    burstRounds: 0,
    jammed: false,
    permanentJam: false,
    clearAttempts: 0,
    fireAccumulator: 0,
    clearHeld: false,
  };
}

/**
 * `mg.c`'s recovered jam hazard for one round, given the threshold in force.
 *
 * Verified byte for byte at `mg.c+0x434` (file `0x1C084`, `RB.EXE` sha256
 * `1b37bda4…`): `excess = (roundsFired << 2) − gunParams[4 + 2·ammo]`, and the
 * shot jams iff `(rand() & 0x3f) < excess`. A 6-bit roll, so the hazard is
 * `excess / 64`, and it reaches certainty at `excess ≥ 64`.
 *
 * `roundsFired` is `ds:0x5e11`, and it is a **burst** counter: `0x1BE73` zeroes
 * it on the first tick the trigger is not held, which is what makes the manual's
 * *"a long burst … has a greater chance of jamming than a short burst"* literally
 * true rather than a figure of speech.
 */
function recoveredJamHazard(originalRounds: number, threshold: number): number {
  const excess = 4 * originalRounds - threshold;
  if (excess <= 0) return 0;
  return Math.min(1, excess / 64);
}

/**
 * How many rounds a burst lasts, on average, before `mg.c`'s hazard stops it.
 *
 * `E[N] = Σ P(survive the first n)`, walked rather than reasoned about — the
 * bail-out lane's rule, *"write the walk, not the paragraph"*. Terminates because
 * the hazard reaches 1 at `n = threshold/4 + 16`.
 */
export function expectedRoundsToJam(threshold: number): number {
  let survival = 1;
  let expected = 0;
  for (let round = 0; survival > 0 && round < 1000; round += 1) {
    expected += survival;
    survival *= 1 - recoveredJamHazard(round, threshold);
  }
  return expected;
}

/** `.DAT` 210/212/214 is indexed by the ammunition: 0 = regular, 1 = incendiary (AD-011 §9.7). */
export function jamThresholdFor(gun: GunSpec, ammunition: AmmunitionType): number {
  return gun.jamThresholdTable[ammunition === "incendiary" ? 1 : 0];
}

/**
 * How much sooner this belt jams than a ball one, as a multiplier on the
 * per-round hazard.
 *
 * ## What is ported here, and what is deliberately not
 *
 * AD-011 §9.7 recovered the whole jam model — the formula, the table and the
 * selector — and the honest thing to say is that **only two thirds of it ships**.
 *
 * The *relative* effect is the original's own and is derived, not chosen:
 * `expectedRoundsToJam` walks `mg.c`'s hazard for each threshold and this is the
 * ratio of the two answers. Regular's 40 gives **15.704** rounds, incendiary's 20
 * gives **10.704**, so an incendiary belt carries **1.4671×** the hazard. Nothing
 * about that number was picked — and it is worth saying that the first draft of
 * this comment reasoned the two figures out on paper as 14.70 and 9.70, which is
 * one round low in each, because summing a survival series by hand is exactly the
 * thing the walk exists to stop anybody doing.
 *
 * The *absolute* rate is still Mobley's, and that is a decision about a genuine
 * conflict in the evidence rather than caution. `mg.c`'s hazard is counted per
 * round, and the port and the original do not fire at the same rate: the gun-audio
 * capture measures the original at **8.47 rounds/s** (one cue key-on per round,
 * `sound-music.md`) against this port's `gunRoundsPerSecond: 55`. Applied per
 * *port* round the formula jams a held trigger in under half a second; applied at
 * the original's own round clock it jams at ~1.7 s, where Mobley — who watched
 * the real game — reports 5–6 s. Three measurements, no two of which agree, and
 * `audio-plan.md` §6b already books the rate discrepancy as a gunnery-lane
 * question. Staking a fleet-wide combat-balance change on picking a winner is not
 * this lane's call to make, so the port keeps the calibrated rate it has and
 * takes the ratio the recovery is unambiguous about.
 *
 * The residual is costed in `gun-mounts-design.md` §11 for whoever closes the
 * rate question; when they do, this function is what they delete.
 */
export function ammunitionJamScale(gun: GunSpec, ammunition: AmmunitionType): number {
  const regular = expectedRoundsToJam(jamThresholdFor(gun, "regular"));
  const chosen = expectedRoundsToJam(jamThresholdFor(gun, ammunition));
  if (chosen <= 0) return 1;
  return regular / chosen;
}

/** Per-round jam probability, rising with burst length and with an incendiary belt. */
export function jamProbability(
  gun: GunSpec,
  burstRounds: number,
  tunables: Tunables,
  ammunition: AmmunitionType = "regular",
): number {
  const thresholdScale = gun.jamThresholdRaw / tunables.gunJamThresholdReference;
  const burstScale = 1 + burstRounds / Math.max(tunables.gunJamBurstScaleRounds, 1);
  return tunables.gunJamBaseProbability * burstScale * thresholdScale * ammunitionJamScale(gun, ammunition);
}

interface JamAttempt {
  readonly jammed: boolean;
  readonly permanentJam: boolean;
  readonly clearAttempts: number;
}

function attemptClear(state: GunState, update: GunUpdate): JamAttempt {
  const { tunables } = update;
  if (update.rng.chance("gunJam", tunables.gunClearProbability)) {
    return { jammed: false, permanentJam: false, clearAttempts: 0 };
  }
  const clearAttempts = state.clearAttempts + 1;
  const escalates =
    clearAttempts >= tunables.gunPermanentJamAttempts &&
    update.rng.chance("gunJam", tunables.gunPermanentJamProbability);
  return { jammed: true, permanentJam: escalates, clearAttempts };
}

interface BurstResult {
  readonly rounds: number;
  readonly burstRounds: number;
  readonly fireAccumulator: number;
  readonly roundsFired: number;
  /** True only on the tick the burst actually jams, so the event fires once. */
  readonly jammedNow: boolean;
}

function fireBurst(state: GunState, update: GunUpdate, jammed: boolean): BurstResult {
  const empty = update.limitedAmmunition && state.rounds <= 0;
  if (!update.firing || jammed || empty) {
    return {
      rounds: state.rounds,
      burstRounds: update.firing ? state.burstRounds : 0,
      fireAccumulator: update.firing ? state.fireAccumulator : 0,
      roundsFired: 0,
      jammedNow: false,
    };
  }

  let accumulator = state.fireAccumulator + update.gun.roundsPerSecond * update.dt;
  let rounds = state.rounds;
  let burstRounds = state.burstRounds;
  let roundsFired = 0;
  let nowJammed = false;
  while (accumulator >= 1 && !nowJammed) {
    if (update.limitedAmmunition && rounds <= 0) break;
    accumulator -= 1;
    // With LIMITED AMMUNITION off the manual promises you "can spray the sky
    // with bullets for as long as you like", so the counter simply does not move.
    if (update.limitedAmmunition) rounds -= 1;
    burstRounds += 1;
    roundsFired += 1;
    if (update.jamsEnabled) {
      nowJammed = update.rng.chance(
        "gunJam",
        jamProbability(update.gun, burstRounds, update.tunables, update.ammunition ?? "regular"),
      );
    }
  }
  return { rounds, burstRounds, fireAccumulator: accumulator, roundsFired, jammedNow: nowJammed };
}

export function updateGuns(state: GunState, update: GunUpdate): GunResult {
  const pressed = update.clearRequested && !state.clearHeld;
  const attempt: JamAttempt =
    pressed && state.jammed && !state.permanentJam
      ? attemptClear(state, update)
      : { jammed: state.jammed, permanentJam: state.permanentJam, clearAttempts: state.clearAttempts };

  const burst = fireBurst(state, update, attempt.jammed);
  return {
    guns: {
      rounds: burst.rounds,
      capacity: state.capacity,
      burstRounds: burst.burstRounds,
      jammed: attempt.jammed || burst.jammedNow,
      permanentJam: attempt.permanentJam,
      clearAttempts: attempt.clearAttempts,
      fireAccumulator: burst.fireAccumulator,
      clearHeld: update.clearRequested,
    },
    roundsFired: burst.roundsFired,
    jammedNow: burst.jammedNow,
    permanentNow: attempt.permanentJam && !state.permanentJam,
  };
}
